import { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import Marquee from "@/components/CSR/Marquee";
import CSRBanner from "@/components/CSR/CSRBanner";
import EventDetail from "@/components/CSR/EventDetail";
import EventProgress from "@/components/CSR/EventProgress";
import EventHighlights from "@/components/CSR/EventHighlights";
import EventHighlightsMobile from "@/components/Mobile/CSR/EventHighlightsMobile";
import DreamPointsDonation from "@/components/CSR/DreamPointsDonation";
import EventRules from "@/components/CSR/EventRules";
import Leaderboard from "@/components/CSR/Leaderboard";
import RaceStates from "@/components/CSR/RaceStates";
import DonationButton from "@/components/CSR/DonationButton";
import { getCookie } from "cookies-next";
import { IAwardInfo, IDonationRate, IEventInfo } from "@/props/IDonation";
import { DREAM_POINT, DREAM_VALUE_DONATION, TRADING_DONATION, TRADING_LOT } from "@/constant/donation";
import {
  getDonationRates as getDonationRatesApi,
  getDonationStates,
  getDonationlatestRecords,
  getEventInfo as getEventInfoApi,
  getAward as getAwardApi,
} from "@/api/donation";
import { europeanDateToUTC } from "@/utils";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import type { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import ClientEventEndContent from "@/components/CSR/ClientEventEndContent";
import { IUserStats } from "@/props/IDonation";
import RewardWall from "@/components/CSR/RewardWall";

// CSR页面
const CSRPage = () => {
  const { t } = useTranslation();
  const [dreamPointRate, setDreamPointRate] = useState<IDonationRate | undefined>();
  const [tradingRate, setTradingRate] = useState<IDonationRate | undefined>();
  const [marqueeData, setMarqueeData] = useState<string[]>([]);
  const [isLogin, setIsLogin] = useState<string | undefined>(undefined);
  const [eventInfo, setEventInfo] = useState<IEventInfo>();
  const { isMobile } = useAppSelector((state: RootState) => state.app);
  const [hasEnded, setHasEnded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const userInfo = useAppSelector((state) => state.user.userInfo);
  const isIB = userInfo?.utype === "ib";
  const [userStats, setUserStats] = useState<IUserStats>();
  const [awardInfo, setAwardInfo] = useState();

  useEffect(() => {
    getMarqueeData();
    getDonationRates();
    getEventInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token: string | undefined = getCookie("token");
    setIsLogin(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCookie("token")]);

  useEffect(() => {
    if (isLogin) {
      getUserStats();
      getAward();
    }
  }, [isLogin]);

  const getUserStats = async () => {
    const data: any = await getDonationStates({ eventId: 1 });
    setUserStats(data);
  };

  const getAward = async () => {
    const data: any = await getAwardApi(1);
    setAwardInfo(data);
  };

  const getEventInfo = async () => {
    const data: any = await getEventInfoApi(1);
    data.startTime = europeanDateToUTC(data.startTime);
    data.endTime = europeanDateToUTC(data.endTime);
    setEventInfo(data);
  };

  useEffect(() => {
    const updateStatus = () => {
      const now = new Date().getTime();
      const start = new Date(eventInfo!.startTime).getTime();
      const end = new Date(eventInfo!.endTime).getTime();
      setHasEnded(now >= end);
      setHasStarted(now >= start);
    };
    if (eventInfo) {
      updateStatus();
    }
  }, [eventInfo]);

  const getDonationUnitInfo = (eventRates, unitType: string) => {
    const unit = eventRates.find((item) => item.unitType === unitType);
    if (unit) {
      return {
        rate: unit.rate,
        unitType: unit.unitType,
        unitValue: unit.unitValue,
      };
    }
  };

  const getDonationRates = async () => {
    const data: any = await getDonationRatesApi({ eventId: 1 });
    setDreamPointRate(getDonationUnitInfo(data, DREAM_POINT));
    setTradingRate(getDonationUnitInfo(data, TRADING_LOT));
  };
  // 捐赠数据/跑马灯
  const getMarqueeData = async () => {
    const data: any = await getDonationlatestRecords({
      limit: 500,
      eventId: 1,
    });
    const finalData = data
      .concat([
        { name: "Tan ****", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.41 },
        { name: "Yang ***", type: "TRADING_DONATION", donationChangeAmountUsd: 0.76 },
        { name: "Azat *********", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.19 },
        { name: "Ping *******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.92 },
        { name: "Chen *****", type: "TRADING_DONATION", donationChangeAmountUsd: 0.55 },
        { name: "Zhao ****", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.7 },
        { name: "Lin ******", type: "TRADING_DONATION", donationChangeAmountUsd: 0.24 },
        { name: "Fan *******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.63 },
        { name: "Lu ********", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.47 },
        { name: "Tian ******", type: "TRADING_DONATION", donationChangeAmountUsd: 0.99 },
        { name: "Huang ********", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.3 },
        { name: "Xu *******", type: "TRADING_DONATION", donationChangeAmountUsd: 0.58 },
        { name: "Huang *******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.78 },
        { name: "Zheng ****", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.15 },
        { name: "Aman *****", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.86 },
        { name: "Li *******", type: "TRADING_DONATION", donationChangeAmountUsd: 0.52 },
        { name: "Pan ******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.22 },
        { name: "Zhou ********", type: "TRADING_DONATION", donationChangeAmountUsd: 0.6 },
        { name: "Liu ******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.39 },
        { name: "Wang ********", type: "TRADING_DONATION", donationChangeAmountUsd: 0.91 },
        { name: "Zhao ********", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.17 },
        { name: "Li ***", type: "TRADING_DONATION", donationChangeAmountUsd: 0.84 },
        { name: "Cang ****", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.4 },
        { name: "Liu ****", type: "TRADING_DONATION", donationChangeAmountUsd: 0.33 },
        { name: "Chen ****", type: "TRADING_DONATION", donationChangeAmountUsd: 0.67 },
        { name: "Wang ****", type: "TRADING_DONATION", donationChangeAmountUsd: 0.95 },
        { name: "Liu **", type: "TRADING_DONATION", donationChangeAmountUsd: 0.21 },
        { name: "Wang *******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.73 },
        { name: "Wang ****", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.5 },
        { name: "Zhou ***", type: "TRADING_DONATION", donationChangeAmountUsd: 0.37 },
        { name: "Pei ******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.81 },
        { name: "Chen ******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.26 },
        { name: "Fan *******", type: "TRADING_DONATION", donationChangeAmountUsd: 0.69 },
        { name: "Lu ********", type: "TRADING_DONATION", donationChangeAmountUsd: 0.43 },
        { name: "Tian ******", type: "DREAM_VALUE_DONATION", donationChangeAmountUsd: 0.88 },
        { name: "Huang ********", type: "TRADING_DONATION", donationChangeAmountUsd: 0.11 },
      ])
      .filter((item) => item.donationChangeAmountUsd !== 0.0);
    const result = finalData.map((item) => {
      let donationMethod = "";
      switch (item.type) {
        case DREAM_VALUE_DONATION:
          donationMethod = t("csr.donate_with_dp");
          break;
        case TRADING_DONATION:
          donationMethod = t("csr.donate_with_trade");
          break;
        default:
          donationMethod = "捐赠了";
      }
      return `${item.name}${donationMethod}${item.donationChangeAmountUsd.toFixed(2)}USD`;
    });
    setMarqueeData(result);
  };

  return (
    <div className="flex w-full flex-col">
      {!hasEnded && <DonationButton isMobile={isMobile} />}
      <CSRBanner isMobile={isMobile} />
      <EventDetail tradingRate={tradingRate!} dreamPointRate={dreamPointRate!} isMobile={isMobile} />
      {hasStarted && !hasEnded && <Marquee items={marqueeData} speed={250} isMobile={isMobile} />}
      {hasEnded && isIB && userStats?.rank && userStats.rank <= 50 && <RewardWall awardInfo={awardInfo as any as IAwardInfo} />}
      {isLogin && hasEnded && !isIB && (
        <ClientEventEndContent
          isMobile={isMobile}
          userStats={userStats as any as IUserStats}
          awardInfo={awardInfo as any as IAwardInfo}
        />
      )}
      {eventInfo && !hasEnded && <EventProgress eventInfo={eventInfo} isMobile={isMobile} />}
      {!hasEnded &&
        (isMobile ? (
          <EventHighlightsMobile isIB={isIB} tradingRate={tradingRate!} />
        ) : (
          <EventHighlights isIB={isIB} tradingRate={tradingRate!} />
        ))}
      {isLogin && (
        <>
          {(!hasEnded || isIB) && (
            <RaceStates
              isIB={isIB}
              isMobile={isMobile}
              isLogin={!!isLogin}
              userStats={userStats as any as IUserStats}
            />
          )}
          {isIB && <Leaderboard isMobile={isMobile} />}
        </>
      )}
      {!hasEnded && (
        <DreamPointsDonation
          dreamPointRate={dreamPointRate!}
          isLogin={!!isLogin}
          isMobile={isMobile}
          hasEnded={hasEnded}
        />
      )}
      {!hasEnded && <EventRules isMobile={isMobile} isIB={isIB} />}
    </div>
  );
};
/** 数据全在客户端取，本函数只加载 i18n 文案，故构建期预渲染 */
export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      data: [1],
      ...(await serverSideTranslations(locale ?? "sc", [process.env.NEXT_PUBLIC_MTS_NS || ""])),
    },
  };
};

export default CSRPage;
